import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Link as MuiLink,
  Skeleton,
} from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invitationService, InvitationInfo } from '../services/invitationService';
import { useAuthStore } from '../store/authStore';
import type { UserRole } from '../store/authStore';
import { tokens } from '../theme';
import { AuthLayout, PasswordToggle } from '../components/auth/AuthLayout';

type Step = 'loading' | 'password' | 'verify' | 'invalid' | 'done';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

const STEP_COPY: Record<Step, { title: string; description?: string }> = {
  loading: { title: 'Zaproszenie do zespołu', description: 'Sprawdzamy Twoje zaproszenie…' },
  password: { title: 'Załóż konto' },
  verify: { title: 'Potwierdź adres e-mail' },
  invalid: { title: 'Zaproszenie jest nieaktywne' },
  done: { title: 'Konto jest już aktywne', description: 'To zaproszenie zostało wykorzystane. Zaloguj się swoim e-mailem i hasłem.' },
};

const StateIcon: React.FC<{ children: React.ReactNode; tone?: 'plum' | 'warning' }> = ({ children, tone = 'plum' }) => (
  <Box
    sx={{
      width: 52,
      height: 52,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      mb: 3,
      bgcolor: tone === 'warning' ? tokens.warningSoft : tokens.plumSoft,
      color: tone === 'warning' ? tokens.warningText : tokens.plum,
      '& .MuiSvgIcon-root': { fontSize: 26 },
    }}
  >
    {children}
  </Box>
);

export const InviteAcceptPage: React.FC = () => {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [step, setStep] = useState<Step>('loading');
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState('');

  // password step
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // verify step
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  useEffect(() => {
    let active = true;
    invitationService
      .get(token)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        setStep(data.step === 'password' ? 'password' : data.step === 'verify' ? 'verify' : data.step === 'done' ? 'done' : 'invalid');
      })
      .catch((e) => {
        if (!active) return;
        setError(errMsg(e, 'Nie udało się wczytać zaproszenia'));
        setStep('invalid');
      });
    return () => {
      active = false;
    };
  }, [token]);

  const pwValid = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);
  const canSubmitPw = pwValid && password === confirm && !submitting;

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitPw) return;
    setError('');
    setSubmitting(true);
    try {
      await invitationService.accept(token, password);
      setStep('verify');
    } catch (err) {
      setError(errMsg(err, 'Nie udało się utworzyć konta'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || verifying) return;
    setError('');
    setVerifying(true);
    try {
      const res = await invitationService.verify(token, code);
      login(res.accessToken, {
        id: res.user.id,
        email: res.user.email,
        role: res.user.role as UserRole,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(errMsg(err, 'Nieprawidłowy kod'));
    } finally {
      setVerifying(false);
    }
  };

  const resend = useCallback(async () => {
    setResendMsg('');
    setError('');
    try {
      const res = await invitationService.resendCode(token);
      setResendMsg(res.message);
    } catch (err) {
      setError(errMsg(err, 'Nie udało się wysłać kodu'));
    }
  }, [token]);

  const hasSalon = !!info?.salonName && step !== 'invalid';
  const eyebrow = hasSalon ? (
    <>
      Zaproszenie do zespołu · {info?.salonName}
      {info?.roleLabel ? ` · ${info.roleLabel}` : ''}
    </>
  ) : undefined;

  const copy = STEP_COPY[step];
  const description =
    step === 'password' ? (
      <>Cześć {info?.firstName}, ustaw hasło, aby dołączyć do zespołu salonu.</>
    ) : step === 'verify' ? (
      <>
        Wysłaliśmy 6-cyfrowy kod na adres <Box component="b" sx={{ color: tokens.ink }}>{info?.email}</Box>. Wpisz go, aby aktywować konto.
      </>
    ) : (
      copy.description
    );

  // Przy bledzie wczytania (np. nieistniejacy link) pokazujemy komunikat API zamiast tekstu domyslnego.
  const invalidReason = error ? `${error.replace(/[.!]+$/, '')}.` : 'Link wygasł, został anulowany albo jest nieprawidłowy.';

  return (
    <AuthLayout
      eyebrow={eyebrow}
      title={copy.title}
      description={step === 'invalid' ? `${invalidReason} Poproś właściciela salonu o wysłanie nowego zaproszenia.` : description}
      icon={
        step === 'invalid' ? <StateIcon tone="warning"><LinkOffOutlinedIcon /></StateIcon>
          : step === 'done' ? <StateIcon><VerifiedUserOutlinedIcon /></StateIcon>
            : undefined
      }
      tagline={hasSalon ? `Dołącz do zespołu ${info?.salonName} — grafik, wizyty i czas pracy w jednym miejscu.` : undefined}
    >
      {error && step !== 'invalid' && (
        <Alert severity="error" role="alert" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      {step === 'loading' && (
        <Stack spacing={2.5} aria-busy="true" aria-label="Ładowanie zaproszenia">
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={50} />
        </Stack>
      )}

      {step === 'invalid' && (
        <Button component={Link} to="/login" variant="outlined" size="large" fullWidth>
          Przejdź do logowania
        </Button>
      )}

      {step === 'done' && (
        <Button component={Link} to="/login" variant="contained" size="large" fullWidth>
          Zaloguj się
        </Button>
      )}

      {step === 'password' && (
        <form onSubmit={submitPassword}>
          <Stack spacing={2.5}>
            <TextField label="E-mail" value={info?.email ?? ''} fullWidth disabled helperText="Na ten adres przyszło zaproszenie" />
            <TextField
              label="Hasło"
              type={showPw ? 'text' : 'password'}
              fullWidth
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={password.length > 0 && !pwValid}
              helperText="Co najmniej 8 znaków, w tym wielka litera i cyfra"
              autoComplete="new-password"
              InputProps={{ endAdornment: <PasswordToggle visible={showPw} onToggle={() => setShowPw((v) => !v)} /> }}
            />
            <TextField
              label="Powtórz hasło"
              type={showPw ? 'text' : 'password'}
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={confirm.length > 0 && confirm !== password}
              helperText={confirm.length > 0 && confirm !== password ? 'Hasła nie są takie same' : undefined}
              autoComplete="new-password"
            />
            <Button type="submit" variant="contained" size="large" fullWidth disabled={!canSubmitPw} sx={{ mt: 1 }}>
              {submitting ? 'Tworzenie konta…' : 'Załóż konto'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={submitCode}>
          <Stack spacing={2.5}>
            {resendMsg && <Alert severity="info">{resendMsg}</Alert>}
            <TextField
              label="Kod z e-maila"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              sx={{
                '& input': {
                  letterSpacing: '0.5em',
                  fontSize: '1.375rem',
                  fontWeight: 650,
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  // wyrownanie wizualne: odstep po ostatniej cyfrze przesuwa tekst w lewo
                  pl: '0.5em',
                },
              }}
              placeholder="000000"
            />
            <Button type="submit" variant="contained" size="large" fullWidth disabled={code.length !== 6 || verifying} sx={{ mt: 1 }}>
              {verifying ? 'Weryfikacja…' : 'Aktywuj konto'}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ mt: 3, pt: 3, borderTop: `1px solid ${tokens.line}`, color: tokens.inkSoft }}>
            Kod nie dotarł? Sprawdź folder spam albo{' '}
            <MuiLink component="button" type="button" onClick={resend} sx={{ verticalAlign: 'baseline', fontSize: 'inherit' }}>
              wyślij go ponownie
            </MuiLink>
          </Typography>
        </form>
      )}
    </AuthLayout>
  );
};

export default InviteAcceptPage;
